import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeaturesProvider } from './contexts/FeaturesContext';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import Layout from './components/Layout';
import GaragePage from './pages/GaragePage';
import VehicleDashboard from './pages/VehicleDashboard';
import AlertsPage from './pages/AlertsPage';
import ProfessionalsPage from './pages/ProfessionalsPage';
import ReportsPage from './pages/ReportsPage';
import ProfilePage from './pages/ProfilePage';
import ServicesPage from './pages/ServicesPage';
import AdminDashboard from './pages/AdminDashboard';
import ProDashboard from './pages/ProDashboard';
import { TermsPage, PrivacyPage } from './pages/LegalPages';
import PartnersPage from './pages/PartnersPage';
import NotFoundPage from './pages/NotFoundPage';
// [NEW] Repasse de veículo entre contas — preview público + aceite. Ver TransferAccept.jsx.
import TransferAccept from './pages/TransferAccept';
// [NEW] Link curto e "amigável" de foto/vídeo de comprovação de serviço
// (usenico.com.br/m/<código>) — pensado pro relatório de venda impresso em papel
// (ver ReportsPage.jsx). Ver MediaLinkPage.jsx.
import MediaLinkPage from './pages/MediaLinkPage';
import usePageTracking from './hooks/usePageTracking';

function Loading() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
      height:'100vh', background:'#f4ede0', flexDirection:'column', gap:'16px',
      fontFamily:"'DM Sans',sans-serif" }}>
      <img src="/nico-dog-face.png" alt="NICO"
        style={{ width:'64px', height:'64px', objectFit:'contain' }} />
      <span style={{ color:'#14562e', fontWeight:700, fontSize:'20px' }}>NICO</span>
      <span style={{ color:'#6a7268', fontSize:'14px' }}>Carregando...</span>
    </div>
  );
}

function Guard({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Loading />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function Page({ children }) {
  return (
    <Guard>
      <Layout>
        {children}
      </Layout>
    </Guard>
  );
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  usePageTracking();
  if (loading) return <Loading />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/garagem" replace />} />
      <Route path="/reset-password" element={<LoginPage />} />
      <Route path="/termos" element={<TermsPage />} />
      <Route path="/privacidade" element={<PrivacyPage />} />
      <Route path="/para-oficinas" element={<PartnersPage />} />
      {/* [NEW] Pública de propósito — sem Guard/Page. Quem recebe o link de repasse pode
          nem ter conta ainda; a própria TransferAccept.jsx decide o que mostrar (preview,
          pedido de login, ou botão de aceitar) dependendo se já está autenticado. Colocar
          isso dentro de <Page> faria o Guard redirecionar pra /login e perder o token da
          URL antes da página sequer renderizar. */}
      <Route path="/repasse/:token" element={<TransferAccept />} />
      {/* [NEW] Também pública, mesmo raciocínio do /repasse acima: quem abre um link
          curto de foto/vídeo (usenico.com.br/m/<código>) — a partir de um relatório
          impresso, por exemplo — pode nem ter conta no NICO. */}
      <Route path="/m/:code" element={<MediaLinkPage />} />

      {/* Telas internas — Layout (sidebar + topbar) envolve cada página */}
      <Route path="/garagem" element={<Page><GaragePage /></Page>} />
      <Route path="/garagem/:id" element={<Page><VehicleDashboard /></Page>} />
      <Route path="/alertas" element={<Page><AlertsPage /></Page>} />
      <Route path="/profissionais" element={<Page><ProfessionalsPage /></Page>} />
      <Route path="/relatorios" element={<Page><ReportsPage /></Page>} />
      <Route path="/servicos" element={<Page><ServicesPage /></Page>} />
      <Route path="/perfil" element={<Page><ProfilePage /></Page>} />
      {/* [FIX] "/pro" ficava só com <Guard>, sem <Page>/<Layout> — por isso a sidebar
          sumia ao entrar em "Minha oficina". Agora segue o mesmo padrão das demais
          rotas internas. */}
      <Route path="/pro" element={<Page><ProDashboard /></Page>} />
      {/* Admin tem login próprio — totalmente independente */}
      <Route path="/admin-nico" element={<AdminDashboard />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <FeaturesProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" toastOptions={{
            style: { background:'#183d24', color:'#fff', borderRadius:'12px',
              fontFamily:"'DM Sans',sans-serif" },
          }} />
        </BrowserRouter>
      </FeaturesProvider>
    </AuthProvider>
  );
}
